import * as React from 'react';
import _ from 'lodash';
import classNames from 'classnames';
import AddCommentForm from '../tabs/collections/AddCommentForm';
import Draggable from 'react-draggable';
import CommentUtil from '../util/CommentUtil';
import { AnnotationThread, RepoItem } from '../autogenerated/ZoomBridge';
import Comment from '../tabs/collections/Comment2';
import FileInfoUtil from '../util/FileInfoUtil';
import { ThemeSetup } from '../themes/ThemeSetup';

import './SpatialComment.css';
import { isMobile } from '../util/ScreenUtil';
import AnnotationUtil from '../queries/annotations/AnnotationUtil';
const debug = require('../debug/debug')('wc:comments:SpatialComment');

type Props = {
  asset: RepoItem,
  comment: AnnotationThread,
  isActive: boolean,
  isHighlighted: boolean,
  style: Object,
  onClose: Function,
  onSubmit: Function,
  onMarkerClick: Function,
  saveAnnotation: Function,
  resolveAnnotation: Function,
  deleteAnnotation: Function,
  saveReply: Function,
  deleteReply: Function,
  onMoveComment: Function
};

type State = {
  isEditing: boolean,
  show: boolean,
  top: boolean,
  left: boolean
};

class SpatialComment extends React.Component<Props, State> {
  state = {
    isEditing: false,
    show: false,
    top: false,
    left: false
  };

  _node: HTMLDivElement = null;
  _commentNode: HTMLDivElement = null;

  componentDidMount() {
    this._isMounted = true;
  }
  componentWillUnmount() {
    this._isMounted = false;
  }

  onDrop = (e, data) => {
    const { comment, onMoveComment } = this.props;

    const { x, y } = data;
    if (!(x || y)) {
      return;
    }

    onMoveComment(comment, x, y);
    this.onMarkerClick(e);
  };

  onMarkerClick = (e) => {
    const { comment, onMarkerClick } = this.props;
    e.stopPropagation();
    onMarkerClick(comment);
  };

  onEditClick = () => {
    this.setState({ isEditing: true });
  };

  onSubmit = async (comment: AnnotationThread) => {
    try {
      await this.props.onSubmit(comment);
      if (this._isMounted) {
        this.setState({ isEditing: false });
      }
    } catch (err) {
      debug('[onSubmit()] error: ', err);
    }
  };

  onClose = () => {
    if (this.state.isEditing) {
      this.setState({ isEditing: false, show: false });
    }

    this.props.onClose();
  };

  onCommentClick = (e: MouseEvent) => {
    debug('[onCommentClick()] calling stopPropagation');
    e.stopPropagation();
  };

  onRef = (inst: ReactElement) => {
    setTimeout(() => {
      if (!(inst && this._node && this._commentNode)) {
        return;
      }

      const node = this._commentNode.firstElementChild;
      if (!node) {
        return;
      }

      const parent = this._node.parentElement;
      if (!parent) {
        return;
      }

      const rect = node.getBoundingClientRect();
      const parentRect = parent.getBoundingClientRect();
      this.parentHeight = parentRect.height;
      const nextState = { show: true };
      if (rect.right > parentRect.right && rect.left - parentRect.left > rect.width) {
        nextState.left = true;
      }
      if (rect.bottom > parentRect.bottom && rect.top - parentRect.top > rect.height) {
        nextState.top = true;
      }

      this.setState(nextState);

      const textarea = this._node.querySelector('textarea');
      if (textarea && textarea.focus) {
        textarea.focus();
      }
    }, 0);
  };

  renderAddForm = () => {
    const { comment } = this.props;

    const { isEditing, show } = this.state;

    const props = {
      ref: this.onRef,
      comment,
      edit: isEditing,
      closeOnOutsideClick: false,
      onSubmit: this.onSubmit,
      onClose: this.onClose,
      style: { visibility: show ? '' : 'hidden' }
    };

    return <AddCommentForm {...props} />;
  };

  renderBrowseForm = () => {
    const {
      asset,
      comment,
      onClose,
      onSubmit,
      resolveAnnotation,
      deleteAnnotation,
      saveReply,
      deleteReply,
      height
    } = this.props;

    const props = {
      ket: comment.getThreadId(),
      ref: this.onRef,
      parentHeight : this.parentHeight,
      comment,
      showCommentIndex: false,
      isAudioOrVideo: FileInfoUtil.isAudioOrVideo(asset),
      style: { visibility: this.state.show ? '' : 'hidden' },
      onClick: _.noop,
      onCancelClick: onClose,
      onSaveCommentClick: onSubmit,
      onDeleteCommentClick: deleteAnnotation,
      onSaveReplyClick: saveReply,
      onDeleteReplyClick: deleteReply,
      onResolveChange: resolveAnnotation,
      onEditing: this.onEditClick,
      height: height,
      top: this.state.top
    };
    return <Comment {...props} />;
  };

  commentDivHorizontalPostion = () => {
    let commentStyle = {};
    if (!(this._node && this._commentNode)) {
      return;
    }
    const parent = this._node.parentElement;
    if (!parent) {
      return;
    }
    const parentRect = parent.getBoundingClientRect();
    let xAxisPosition;
    if (_.isString(this.props.style.left)) {
      xAxisPosition = (this.props.style.left.replace('px', '') * 100) / parentRect.width + 3;
    } else {
      xAxisPosition = (this.props.style.left * 100) / parentRect.width + 3;
    }
    let yAxisPosition = 100;
    if (!this.state.top) {
      commentStyle = { transform: `translateX(-${xAxisPosition}%)`, overflow: 'auto' };
    } else {
      commentStyle = { transform: `translate(-${xAxisPosition}%,-${yAxisPosition}%)`, overflow: 'auto' };
    }

    return commentStyle;
  };

  render() {
    const { comment, style, isActive, isHighlighted } = this.props;

    const { isEditing, top, left } = this.state;

    const dragProps = {
      axis: 'both',
      bounds: 'parent',
      onStop: this.onDrop
    };

    const canDrag = CommentUtil.isOwnerOrAdmin(comment) || CommentUtil.isCreator(comment);
    const isResolvedComment = AnnotationUtil.isResolved(comment);

    const markerStyle = {
      background: CommentUtil.commentBackgroundByKind(comment),
      backgroundImage: isResolvedComment && !isActive ? ThemeSetup.getBackgroundURL('checkmark_white.svg') : '',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: '50%'
    };

    const className = classNames('comment-marker marker', {
      active: isActive,
      unread: !isActive && CommentUtil.isAnyCommentUnreadInThread(comment),
      resolved: isResolvedComment,
      highlighted: isHighlighted
    });
    const marker = (
      <div className={className} style={markerStyle} onClick={this.onMarkerClick} onTouchEnd={this.onMarkerClick}>
        <span>{comment._annotationLocation._commentIndex}</span>
      </div>
    );

    return isActive ? (
      <div
        name="SpatialComment"
        data-commentId={comment._threadId}
        className={classNames({ active: isActive })}
        style={style}
        onClick={this.onCommentClick}
        ref={(node) => (this._node = node)}>
        {marker}
        <div
          className={classNames('comment', { top, left })}
          ref={(node) => (this._commentNode = node)}
          style={this.commentDivHorizontalPostion()}>
          {isEditing || !comment._threadId ? this.renderAddForm() : this.renderBrowseForm()}
        </div>
      </div>
    ) : canDrag && !isResolvedComment ? (
      <Draggable {...dragProps}>
        <div name="SpatialComment" style={style} data-commentId={comment._threadId}>
          {marker}
        </div>
      </Draggable>
    ) : (
      <div name="SpatialComment" style={style}>
        {marker}
      </div>
    );
  }
}

export default SpatialComment;
